import React, { useState, useContext } from 'react';
import { Button, Stack, Typography, Box, Checkbox, FormControlLabel } from '@mui/material';
import Modal from 'components/_layouts/Modal';
import { translate as t } from 'hooks/useTranslations';
import { neutral } from '_lex/_foundations/phxColors';
import useHasAccreditationSolution from 'views/App/Portal/AWB/_hooks/useHasAccreditationSolution';
import { enqueueSnackbar } from 'notistack';
import errorHelper from 'helpers/errorHelper';
import { IssuedManualsContext } from '../../context';
import { IssuedManualDownloadType } from '../../../_types/manuals/issued';
import { ManualItem } from 'views/App/Portal/Policy/_types/manuals/draft';
import { exportIssuedManual } from '../../_data_sources/useExportIssuedManual';
import { downloadFileFromUrl } from 'views/App/Portal/Policy/DraftManuals/helpers/exportHelper';

type ExportModalProps =
  | {
      open: boolean;
      onClose: () => void;
      exportType: 'manual';
      manual: ManualItem;
    }
  | {
      open: boolean;
      onClose: () => void;
      exportType: 'content';
      manualId: string;
      contentId: string;
      contentType: IssuedManualDownloadType;
      contentTitle: string;
    };

const ExportModal: React.FC<ExportModalProps> = (props) => {
  const { open, onClose, exportType } = props;
  const { handleDownload } = useContext(IssuedManualsContext);
  const manualOrgId = props.exportType === 'manual' ? props.manual?.organizationId : undefined;
  const hasAwbSubscription = useHasAccreditationSolution(manualOrgId);
  const [showAccreditationTags, setShowAccreditationTags] = useState(false);
  const [showEditLevelTags, setShowEditLevelTags] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async () => {
    setIsExporting(true);

    try {
      if (exportType === 'manual') {
        const { manual } = props;
        if (!manual?.id) {
          enqueueSnackbar(t('policy.issuedManuals.export.exportError'), { variant: 'error' });
          setIsExporting(false);
          return;
        }

        await exportIssuedManual(
          {
            manualId: manual.id,
            showEditLevelTags,
            showAccreditationTags,
          },
          async (response) => {
            if (response.presignedUrl) {
              // Use polling to wait for the file to be ready
              await downloadFileFromUrl({
                presignedUrl: response.presignedUrl,
                fileName: `${manual.name}_Manual`,
                fileType: 'PDF',
              });
            } else {
              throw new Error('Invalid export response format');
            }
            enqueueSnackbar(t('policy.issuedManuals.export.exportSuccess'), { variant: 'success' });
            setIsExporting(false);
            onClose();
          },
          (error) => {
            console.error('Export error:', error);
            errorHelper.enqueueErrors(error);
            setIsExporting(false);
          }
        );
      } else {
        // Content export
        const { manualId, contentId, contentType, contentTitle } = props;
        await handleDownload(manualId, contentId, contentType, contentTitle, {
          showEditLevelTags,
          showAccreditationTags,
        });
        enqueueSnackbar(t('policy.issuedManuals.export.exportSuccess'), { variant: 'success' });
        setIsExporting(false);
        onClose();
      }
    } catch (error) {
      console.error('Export error:', error);
      const errorMessage =
        error instanceof Error && error.message === 'Time out has been exceeded'
          ? 'Time out has been exceeded'
          : t('policy.issuedManuals.export.exportError');
      enqueueSnackbar(errorMessage, { variant: 'error' });
      setIsExporting(false);
    }
  };

  const getButtonText = () => {
    return isExporting ? t('policy.issuedManuals.export.exporting') : t('policy.issuedManuals.export.export');
  };

  const getTitle = () => {
    return exportType === 'manual'
      ? t('policy.issuedManuals.export.title')
      : t('policy.issuedManuals.export.titleContent');
  };

  const getDescription = () => {
    return exportType === 'manual'
      ? t('policy.issuedManuals.export.description')
      : t('policy.issuedManuals.export.descriptionContent');
  };

  const getItemName = () => {
    if (exportType === 'manual') {
      return props.manual?.name || '';
    }
    return props.contentTitle;
  };

  const getItemLabel = () => {
    return exportType === 'manual'
      ? t('policy.issuedManuals.export.manualName')
      : t('policy.issuedManuals.export.contentName');
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={getTitle()}
      actions={
        <Stack
          direction='row'
          spacing={3}
        >
          <Button
            variant='outlined'
            onClick={onClose}
            disabled={isExporting}
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant='contained'
            onClick={handleExport}
            disabled={isExporting}
            loading={isExporting}
            loadingPosition='end'
          >
            {getButtonText()}
          </Button>
        </Stack>
      }
    >
      <Stack spacing={4}>
        <Box>
          <Typography
            variant='body1'
            sx={{ mb: 2 }}
          >
            {getDescription()}
          </Typography>
          <Stack
            direction='row'
            spacing={1}
            sx={{ alignItems: 'center' }}
          >
            <Typography
              variant='body2'
              sx={{ color: neutral[600] }}
            >
              {getItemLabel()}:
            </Typography>
            <Typography
              variant='body2'
              sx={{ fontWeight: 600, color: neutral[900] }}
            >
              {getItemName()}
            </Typography>
          </Stack>
        </Box>

        <Box>
          {/* Show options title only if the organization has an AWB subscription, deleted after edit tags are re-implemented */}
          {hasAwbSubscription && (
            <Typography
              variant='h6'
              sx={{ mb: 2 }}
            >
              {t('policy.issuedManuals.export.options')}
            </Typography>
          )}
          <Stack spacing={2}>
            {hasAwbSubscription && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showAccreditationTags}
                    onChange={(e) => setShowAccreditationTags(e.target.checked)}
                    disabled={isExporting}
                  />
                }
                label={
                  <Box>
                    <Typography variant='body1'>{t('policy.issuedManuals.export.showAccreditationTags')}</Typography>
                    <Typography
                      variant='body2'
                      sx={{ color: neutral[600] }}
                    >
                      {t('policy.issuedManuals.export.showAccreditationTagsDescription')}
                    </Typography>
                  </Box>
                }
              />
            )}
            {/* Edit-level tags option commented out because the backend 
              does not yet support edit-level tags for issued manuals.
              This will be re-incorporated once the backend functionality is ready.
            */}
            {/* <FormControlLabel
              control={
                <Checkbox
                  checked={showEditLevelTags}
                  onChange={(e) => setShowEditLevelTags(e.target.checked)}
                  disabled={isExporting}
                />
              }
              label={
                <Box>
                  <Typography variant='body1'>{t('policy.issuedManuals.export.showEditLevelTags')}</Typography>
                  <Typography
                    variant='body2'
                    sx={{ color: neutral[600] }}
                  >
                    {t('policy.issuedManuals.export.showEditLevelTagsDescription')}
                  </Typography>
                </Box>
              }
            /> */}
          </Stack>
        </Box>
      </Stack>
    </Modal>
  );
};

export default ExportModal;
